
import React from 'react';
import { Palette, MessageSquare, ExternalLink, Flower, User, Zap } from 'lucide-react';
import { Language } from '../types';

interface CustomRequestProps {
  language: Language;
  onOpenChat: () => void;
}

const CustomRequest: React.FC<CustomRequestProps> = ({ language, onOpenChat }) => {
  const t = {
    pt: {
      title: 'Encomendas Exclusivas',
      sub: 'Obras Únicas sob Medida',
      desc: 'Deseja algo que transcenda a galeria comum? Entre em contato com nossos mestres artesãos para solicitar uma arte personalizada para o seu perfil.',
      niiko: 'Niiko',
      lucine: 'Lucine',
      contactTitle: 'Canais de Contato',
      steamProfile: 'Ver Perfil Steam',
      discordLabel: 'Discord Handle',
      quote: 'Cada pincelada é uma história única.',
      chatBtn: 'Chat'
    },
    en: {
      title: 'Exclusive Commissions',
      sub: 'Unique Custom Works',
      desc: 'Looking for something beyond the common gallery? Contact our master craftsmen to request a personalized art for your profile.',
      niiko: 'Niiko',
      lucine: 'Lucine',
      contactTitle: 'Contact Channels',
      steamProfile: 'View Steam Profile',
      discordLabel: 'Discord Handle',
      quote: 'Every brushstroke is a unique story.',
      chatBtn: 'Chat'
    }
  }[language];

  const masters = [
    {
      name: t.niiko,
      discord: 'annaniiko',
      steam: 'https://steamcommunity.com/id/NiikoCute/',
      role: 'Master Artist',
      color: '#ff9eb5'
    },
    {
      name: t.lucine,
      discord: '_lucine',
      steam: 'https://steamcommunity.com/id/lucine87/',
      role: 'Master Designer',
      color: '#ffffff'
    }
  ];

  return (
    <div className="max-w-5xl mx-auto p-10 bg-zinc-900/40 backdrop-blur-xl border border-white/10 shadow-2xl hover:border-[#ff9eb5]/20 transition-all">
      <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-16 border-b border-white/5 pb-12">
        <div className="flex items-center gap-8">
          <div className="p-6 bg-white shadow-2xl border-b-8 border-[#ff9eb5]">
            <Palette className="w-12 h-12 text-black" />
          </div>
          <div className="text-center md:text-left">
            <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter oil-text">
              {t.title}
            </h2>
            <p className="text-zinc-500 font-bold uppercase text-xs tracking-[0.4em] mt-2">{t.sub}</p>
          </div>
        </div>
        
        <button 
          onClick={onOpenChat}
          className="bg-[#ff9eb5] hover:bg-white text-white hover:text-black font-black px-10 py-5 flex items-center gap-4 transition-all shadow-[0_15px_40px_rgba(255,158,181,0.2)] active:scale-95 border-b-4 border-white/20 uppercase tracking-widest text-xs"
        >
          <Zap className="w-5 h-5 animate-pulse" />
          {t.chatBtn}
        </button>
      </div>

      <p className="text-zinc-400 text-xl leading-relaxed mb-16 max-w-3xl border-l-4 border-[#ff9eb5] pl-8 py-4 italic">
        "{t.desc}"
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
        {masters.map((master, idx) => (
          <div key={idx} className="bg-zinc-950/80 border border-white/5 p-8 group hover:border-[#ff9eb5]/40 transition-all relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#ff9eb5]/5 -translate-y-16 translate-x-16 rounded-full group-hover:scale-150 transition-transform" />
            
            <div className="flex items-center gap-6 mb-8 relative z-10">
              <div className="w-16 h-16 bg-white flex items-center justify-center border-b-4 border-[#ff9eb5]">
                <User className="w-8 h-8 text-black" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-white uppercase tracking-tighter artistic-font">{master.name}</h3>
                <span className="text-[10px] text-zinc-500 font-black uppercase tracking-widest">{master.role}</span>
              </div>
            </div>

            <div className="space-y-6 relative z-10">
              <div className="bg-black/40 p-5 border border-white/5 group-hover:border-[#ff9eb5]/20 transition-all">
                <span className="text-[9px] font-black text-zinc-600 uppercase tracking-widest block mb-2">{t.discordLabel}</span>
                <div className="flex items-center justify-between">
                  <span className="text-white font-mono text-lg font-bold">@{master.discord}</span>
                  <div className="w-8 h-8 bg-zinc-900 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-[#ff9eb5]" />
                  </div>
                </div>
              </div>

              <a 
                href={master.steam} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full bg-zinc-900 hover:bg-white text-zinc-500 hover:text-black py-4 px-6 flex items-center justify-between transition-all border border-zinc-800 hover:border-transparent group/link"
              >
                <span className="text-[10px] font-black uppercase tracking-widest">{t.steamProfile}</span>
                <ExternalLink className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-10 border-t border-zinc-800 flex justify-between items-center">
        <p className="text-sm text-zinc-700 font-black uppercase tracking-widest">{t.quote}</p>
        <Flower className="w-6 h-6 text-[#ff9eb5] opacity-20" />
      </div>
    </div>
  );
};

export default CustomRequest;
